import type { ColorList, PartialDeep } from '@nuvolo/nuux-theme/types';
import type { ExtendedNuvoTheme } from './types';
import { ReactComponent as NuvoloLogoDark } from '#/static/NuvoloLogoDark.svg';

export const darkThemeLogo = NuvoloLogoDark;

export const darkColors: PartialDeep<ExtendedNuvoTheme['colors']> = {
  primary: {
    light3: '#26282B',
    light2: '#0D0F12',
    light: '#0D0F12',
    middle: '#A6A9AD',
    dark: '#FFFFFF',
    dark2: '#FFFFFF',
    dark3: '#FFFFFF',
  },
  secondary: {
    light3: '#1C1C1C',
    light2: '#2C2C2C',
    light: '#6B6B6B',
    middle: '#6B6B6B',
    dark: '#6B6B6B',
    dark2: '#FFFFFF',
    dark3: '#FFFFFF',
  },
  accent: {
    light3: '#621414',
    light2: '#621414',
    light: '#621414',
    middle: '#FF7262',
    dark: '#AC2222',
    dark2: '#F74848',
    dark3: '#F63131',
  },
  ui: {
    light3: '#0D0F12',
    light2: '#353535',
    light: '#353535',
    middle: '#232323',
    dark: '#353535',
    dark2: '#FFFFFF',
    dark3: '#FFFFFF',
  },
  contrast: {
    light: '#FFFFFF',
    dark: '#000000',
  },
  warn: {
    light3: '#241704',
    light2: '#241704',
    light: '#FEA41D',
    middle: '#FEA41D',
    dark: '#FEA41D',
    dark2: '#FEA41D',
    dark3: '#FEA41D',
  },
  error: {
    light3: '#251616',
    light2: '#251616',
    light: '#FC533E',
    middle: '#FC533E',
    dark: '#FC533E',
    dark2: '#FC533E',
    dark3: '#FC533E',
  },
  success: {
    light3: '#06210A',
    light2: '#06210A',
    light: '#25CF3F',
    middle: '#25CF3F',
    dark: '#25CF3F',
    dark2: '#25CF3F',
    dark3: '#25CF3F',
  },
  info: {
    light3: '#0C1A2A',
    light2: '#0C1A2A',
    light: '#1D8AFE',
    middle: '#1D8AFE',
    dark: '#1D8AFE',
    dark2: '#1D8AFE',
    dark3: '#1D8AFE',
  },
  reporting: {
    darkGray: '#F4312A',
    blue: '#FE9801',
    aqua: '#21B937',
    green: '#FDCE01',
    gold: '#04B1AE',
    orange: '#065352',
    red: '#B054DC',
    pink: '#FF00F5',
    purple: '#55C0EF',
    gray: '#5657D9',
    lightBlue: '#FE3661',
    lightAqua: '#5DADC7',

    //missing colors - made them all the last color (#A3835F)
    lightGreen: '#A3835F',
    lightGold: '#A3835F',
    lightOrange: '#A3835F',
    lightRed: '#A3835F',
    // Check missing colors^^^

    lightPink: '#1E1927',
    lightPurple: '#BB69E0',
    lightGray: '#C0C1C5',
    thresholdGreen: '#21B937',
    thresholdRed: '#F4312A',

    // custom wo priority
    priorityCritical: '#F63131',
    priorityHigh: '#FD6108',
    priorityMedium: '#FDCE01',
    priorityLow: '#21B937',
    priorityVeryLow: '#C9F3C9',
    priorityUnknown: '#C0C1C5',

    // custom finding priority
    findPriorityPlaning: '#3176F6',
    findPriorityLow: '#21B937',
    findPriorityMedium: '#FDCE01',
    findPriorityHigh: '#FD6108',
    findPriorityCritical: '#F63131',
    findPriorityUnknown: '#C0C1C5',
  } as ColorList,
  custom: {
    tabsCounterColor: '#16181C',
    constantBlue: '#1D8AFE',
    constantIconBlue: '#3176F6',
    constantTeal: '#6BB5CC',
    constantGray: '#A5A7A7',
    link: '#55C0EF',
    cardBackground: '#0D0F12',
    formBackground: '#0D0F12',
    paginationText: '#FFFFFF',
    dataGridBackground: '#151719',
    dataGridPageBackground: '#0D0F12',
    formGroupBackground: '#151719',
    dataGridHeaderBackground: '#26282B',
    columnChooserBackground: '#363739',
    dataGridHeaderText: '#FFFFFF',
    selectedBackground: '#26282b',
    loaderColor: '#A6A9AD',
    hoveredBackground: '#35373A',
    contrastText: '#FFFFFF',
    invertedText: '#1C1C1C',
    invertedBackground: '#000000',
    lightContrastText: '#6B6B6B',
    formInputBackground: '#212325',
    formInputBorder: '#2c2e30',
    formInputPlaceholderColor: '#A6A9AD',
    formDisabledInputColor: '#7B7E83',
    inputIconBackground: '#2C2C2C',
    switchBackground: '#212325',
    chipSecondaryBackground: '#1C1C1C',
    chipSecondaryText: '#C4C4C4',
    defaultIconBackground: '#1C1C1C',
    fancyDropDownBackground: '#151719',
    focusItemBackground: '#212325', // Tmm select card mostly
    selected: '#0A0A0A', // mobile list cards selected border color (inverted for dark mode)
    hoverColor: '#25272A', // app menu hover color definition (lighter for dark mode)
    dashboardConfigItemBorder: '#212326',
    formImageWrapperBackground: '#121416',

    // Gray tones (adjusted for dark mode)
    labelGray: '#FFFFFF', //profile menu labels
    mediumGray: '#6A6C71', // organization wrapper, subtle text (lighter for dark mode)
    iconGray: '#FFFFFF', // icon colors (lighter for dark mode)
    tabGray: '#CDCDCD', // tab backgrounds (lighter for dark mode)
    badgeGray: '#EAEAEA', // badge backgrounds (lighter for dark mode)
    constLightGray: '#A6A9AD', // color for the "x" clear button in reference fields

    // Borders
    borderLight: '#2F2F2F', // light borders (darker for dark mode)

    // Status backgrounds (slightly darker versions for dark mode)
    statusPlanned: '#3F3D2F', // dark yellow background for planned status
    statusWoCreated: '#302039', // dark purple background for wo_created status

    // Accent colors (adjusted for dark mode)
    blueAccent: '#4D8FFF', // blue accent for selections (lighter for dark mode)
    blueBackground: '#1A2F3F', // dark blue background
  },
};
